import React, { useState, useEffect } from "react";
import Button from "./base/Button.jsx";
import RequirementLicense from "./RequirementLicense";
import RequirementDocument from "./RequirementDocument";
import { PlusIcon } from "@heroicons/react/24/outline";

export default function RequirementsPanel({ requirements, setRequirements }) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [licenseChoice, setLicenseChoice] = useState(
    requirements.licenseChoice || { name: "none", label: "Aucun", value: "none" }
  );
  
  const [documents, setDocuments] = useState(
    requirements.documents || []
  );

  const [customDocName, setCustomDocName] = useState("");
  const [duplicateMessage, setDuplicateMessage] = useState("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleLicenseChange = (newChoice) => {
    setLicenseChoice(newChoice);
    setRequirements({
      licenseChoice: newChoice,
      documents,
    });
  };

  const handleDocumentLevelChange = (documentId, newLevel) => {
    const updatedDocuments = documents.map((doc) =>
      doc.id === documentId ? { ...doc, level: newLevel } : doc
    );
    setDocuments(updatedDocuments);
    setRequirements({
      licenseChoice,
      documents: updatedDocuments,
    });
  };

  const handleDeleteDocument = (documentId) => {
    const updatedDocuments = documents.filter((doc) => doc.id !== documentId);
    setDocuments(updatedDocuments);
    setRequirements({
      licenseChoice,
      documents: updatedDocuments,
    });
  };

  const handleAddDocument = (document_lbl) => {
    const newDocument = {
      id: Date.now(),
      id_bdd: Date.now(),
      type: document_lbl,
      label: document_lbl,
      level: "optional",
    };
    const updatedDocuments = [...documents, newDocument];
    setDocuments(updatedDocuments);
    setRequirements({
      licenseChoice,
      documents: updatedDocuments,
    });
  };

  const handleAddCustomDocument = () => {
    if (customDocName.trim()) {
      const docNameLower = customDocName.trim().toLowerCase();
      const exists = documents.some(doc => doc.label?.toLowerCase() === docNameLower);
      
      if (exists) {
        setDuplicateMessage("Ce document existe déjà");
        setTimeout(() => setDuplicateMessage(""), 3000);
      } else {
        handleAddDocument(customDocName.trim());
        setCustomDocName("");
        setDuplicateMessage("");
      }
    }
  };

  console.log("Current requirements state:", { requirements});

  return (
    <div style={styles.panelContainer}>
      <div style={{ ...styles.columnsWrapper, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
        <div style={styles.column}>
            <div style={styles.documentsHeader}>
                <h3 style={styles.documentsTitle}>Licence</h3>
            </div>
            <RequirementLicense
                licenseChoice={licenseChoice}
                onLicenseChange={handleLicenseChange}
            />
        </div>

        <div style={styles.column}>
          <div style={styles.documentsHeader}>
            <h3 style={styles.documentsTitle}>Documents supplémentaires</h3>
          </div>

          {documents.length > 0 ? (
            documents.map((doc) => (
              <RequirementDocument
                key={doc.id}
                id={doc.id}
                documentType={doc.type}
                label={doc.label}
                level={doc.level}
                onLevelChange={(newLevel) =>
                  handleDocumentLevelChange(doc.id, newLevel)
                }
                onDelete={() => handleDeleteDocument(doc.id)}
              />
            ))
          ) : (
            <p style={styles.noDocumentsText}>
              Aucun document supplémentaire ajouté
            </p>
          )}

          <div style={styles.addSection}>
            <div style={styles.customSection}>
              <label style={styles.customLabel}>Ajouter un document :</label>
              <div style={styles.customInputGroup}>
                <input
                  type="text"
                  value={customDocName}
                  onChange={(e) => setCustomDocName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddCustomDocument();
                    }
                  }}
                  placeholder="Ex: Certificat médical..."
                  style={styles.customInput}
                />
                <Button
                  icon={PlusIcon}
                  title="Ajouter"
                  onClick={handleAddCustomDocument}
                  disabled={!customDocName.trim()}
                  style="dark"
                  width="auto"
                />
              </div>
              {duplicateMessage && (
                <p style={styles.duplicateMessage}>{duplicateMessage}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  panelContainer: {
    padding: "20px",
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  columnsWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "30px",
  },
  column: {
    display: "flex",
    flexDirection: "column",
  },
  documentsHeader: {
    marginBottom: "16px",
  },
  documentsTitle: {
    margin: "0 0 12px 0",
    fontSize: "16px",
    fontWeight: "600",
    color: "#1f2937",
  },
  noDocumentsText: {
    margin: "16px 0",
    fontSize: "14px",
    color: "#9ca3af",
    fontStyle: "italic",
  },
  addSection: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
  },
  selectSection: {
    marginBottom: "16px",
  },
  selectLabel: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "#6b7280",
    marginBottom: "8px",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    color: "#1f2937",
  },
  customSection: {
    marginTop: "12px",
  },
  customLabel: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "#6b7280",
    marginBottom: "8px",
  },
  customInputGroup: {
    display: "flex",
    gap: "8px",
  },
  customInput: {
    flex: 1,
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    color: "#1f2937",
  },
  customButton: {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "600",
    backgroundColor: "#10b981",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  duplicateMessage: {
    marginTop: "8px",
    fontSize: "13px",
    color: "#ef4444",
    fontWeight: "500",
  },
};
